import { useSession } from "providers/SessionProvider";
import { IIncomeEntry } from "types";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { MdArrowForward, MdInfoOutline } from "react-icons/md";
import MobileFooterLayout from "../../layout/MobileFooterLayout";
import { typographyStyles } from "constants/styles";
import { DatePicker } from "@mui/x-date-pickers";
import { toDateValue } from "helpers";

interface IncomeEntryEditProps {
  incomeEntry: IIncomeEntry;
  setIncomeEntry: Dispatch<SetStateAction<IIncomeEntry | null>>;
  nextStep: (i: IIncomeEntry) => void;
  loading: boolean;
}

export default function IncomeEntryEdit({
  incomeEntry,
  setIncomeEntry,
  nextStep,
  loading,
}: IncomeEntryEditProps) {
  const { stores } = useSession();

  const storeOptions = useMemo(
    () => [
      { id: 0, label: "Todos" },
      ...stores
        .filter((s) => s.is_active)
        .map((s) => ({
          id: s.id,
          label: s.description,
        })),
    ],
    [stores]
  );

  const [actualStore, setActualStore] = useState<{ id: number; label: string } | null>(() => {
    const foundedStore = stores.find(store => store.id === incomeEntry.store_id) ?? null;
    if(!foundedStore) return { id: 0, label: "Todos" };
    return { id: foundedStore.id ?? 0, label: foundedStore.description ?? "" };
  });

  const [oc, setOc] = useState(incomeEntry.articles?.[0]?.bundles?.[0].oc ?? '');
  const [errors, setErrors] = useState<
    Array<"fecha_ingreso" | "store_id" | "lote_interno">
  >([]);

  const clearError = (field: "fecha_ingreso" | "store_id" | "lote_interno") => {
    setErrors((current) => current.filter((error) => error !== field));
  };

  const handleChangeData = (
    field: keyof IIncomeEntry,
    value: any
  ) => {
    if(field === "store_id") {
      value = value?.id ?? null;
      clearError("store_id");
      const foundedStore = stores.find(store => store.id === value) ?? null;
      setIncomeEntry((prev) => {
        if(!prev) return null;
        const next = {
          ...prev,
          store_id: value,
          store: foundedStore,
          // reset article bundle locations and arrived when store changes
          articles: prev.articles?.map(article => ({
            ...article,
            bundles: article.bundles?.map(bundle => ({
              ...bundle,
              arrived: null,
              location_id: null,
              location: null,
            })),
          })),
        };
        setActualStore(foundedStore ? { id: foundedStore.id ?? 0, label: foundedStore.description ?? "" } : { id: 0, label: "Todos" });
        return next;
      });
      return;
    }
    if (field === "fecha_ingreso" || field === "lote_interno") {
      clearError(field);
    }
    setIncomeEntry((prev) => {
      if(!prev) return null;
      const next = {
        ...prev,
        [field]: value,
      };
      return next;
    });
  }

  const handleConfirm = () => {
    const nextErrors: Array<"fecha_ingreso" | "store_id" | "lote_interno"> = [];
    if (!incomeEntry.fecha_ingreso) nextErrors.push("fecha_ingreso");
    if (!incomeEntry.store_id) nextErrors.push("store_id");
    if (!incomeEntry.lote_interno?.trim()) nextErrors.push("lote_interno");

    setErrors(nextErrors);
    if (nextErrors.length > 0) return;

    setIncomeEntry((prev) => {
      if(!prev) return null;
      const next = {
        ...prev,
        // propagate oc in bundles
        articles: prev?.articles?.map(article => ({
          ...article,
          bundles: article.bundles?.map(bundle => ({
            ...bundle,
            oc: oc,
          })),
        })),
      }
      nextStep(next);
      return next;
    });
  }

  return (
    <Stack gap={1}>
      <Box sx={{ pb: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={typographyStyles.monospace}
        >
          Remito {incomeEntry.numero_remito}
        </Typography>
        <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
          {incomeEntry.client.razon_social}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Transporte: {incomeEntry.associated_client?.razon_social || "No especificado"}
        </Typography>
  
        <Chip
          icon={<MdInfoOutline />}
          label="Datos del Ingreso"
          color="secondary"
          variant="outlined"
          sx={{ mt: 1.5 }}
        />
  
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1.75, lineHeight: 1.5 }}
        >
          Tocá cada fardo para tildarlo. Si encontrás un problema, tocá la fila y
          elegí el motivo. El producto se cierra solo al resolver todos sus
          fardos.
        </Typography>
      </Box>
      {/* Datos del remito */}
      <Card 
        elevation={0}
        sx={{ p: 1, bgcolor: "divider" }}
      >
        <Typography sx={{ fontWeight: 500, color: "text.secondary" }}>
          DATOS DEL REMITO
        </Typography>
        <FormControl fullWidth>
          <TextField
            label="Cliente asociado (emisor)"
            value={incomeEntry.associated_client?.razon_social ?? ""}
            InputProps={{
              readOnly: true,
            }}
            />
        </FormControl>
        <FormControl fullWidth>
          <DatePicker
            label="Fecha de ingreso"
            value={toDateValue(incomeEntry.fecha_ingreso?.slice(0, 10))}
            onChange={(newValue) =>
              handleChangeData('fecha_ingreso', newValue ? newValue.toISOString().slice(0, 10) : null)
            }
            slotProps={{
              textField: {
                required: true,
                error: errors.includes("fecha_ingreso"),
              }
            }}
          />
        </FormControl>
      </Card>

      {/* Lote interno */}
      <Card
        elevation={0}
        sx={{ p: 1, bgcolor: "divider" }}
      >
        <Typography sx={{ fontWeight: 500, color: "text.secondary" }}>
          LOTE INTERNO
        </Typography>
        <FormControl fullWidth sx={{ gap: 1 }}>
          <TextField
            label="Lote interno por SKU"
            value={incomeEntry.lote_interno ?? ""}
            onChange={(e) => handleChangeData("lote_interno", e.target.value)}
            required
            error={errors.includes("lote_interno")}
          />
        </FormControl>
        <Typography color="text.secondary" fontSize="0.8rem">
          Se aplica a todos los fardos del ingreso.
        </Typography>
      </Card>

      {/* Almacen */}
      <Card 
        elevation={0}
        sx={{ p: 1, bgcolor: "divider" }}
      >
        <Typography sx={{ fontWeight: 500, color: "text.secondary" }}>
          ALMACÉN
        </Typography>
        <FormControl fullWidth>
          <Autocomplete
            fullWidth
            autoComplete
            value={actualStore}
            options={storeOptions}
            isOptionEqualToValue={(option, value) =>
              option.id === value.id
            }
            onChange={(event, newValue) => handleChangeData('store_id', newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="Almacén de destino"
                error={errors.includes("store_id")}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                {option.label}
              </li>
            )}
            blurOnSelect
          />
        </FormControl>
      </Card>

      {/* Orden de compra */}
      <Card 
        elevation={0}
        sx={{ p: 1, bgcolor: "divider" }}
      >
        <Typography sx={{ fontWeight: 500, color: "text.secondary" }}>
          ORDEN DE COMPRA
        </Typography>
        <FormControl fullWidth sx={{gap: 1}}>
          <TextField
            label="OC del ingreso"
            value={oc}
            onChange={(e) => setOc(e.target.value)}
          />
        </FormControl>
        <Typography color="text.secondary" fontSize="0.8rem" >
          Se aplica a todo el ingreso. Si algún artículo tiene OC distinta, se ajusta en la verificación.
        </Typography>
      </Card>
      <Box sx={{ height: "5.2rem", display: { xs: "block", md: "none" } }}/>
      <MobileFooterLayout>
        <Button
          variant="contained"
          color="info"
          fullWidth
          size="large"
          disabled={loading}
          onClick={handleConfirm}
          sx={{ py: 1.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}
        >
          {loading ? <CircularProgress color="info" size={24}/> : (<>
            Continuar a verificación <MdArrowForward size={20}/>
          </>)}
        </Button>
      </MobileFooterLayout>
    </Stack>
  )
}
